import { createStep } from '@/guides/helpers/guide-editor.util';
// eslint-disable-next-line no-restricted-imports
import store from '@/vuex/store';
import { $can } from '@pendo/permissions';
import { BuildingBlock, BuildingBlockLayouts } from '@pendo/services/BuildingBlocks';
import keyBy from 'lodash/keyBy';

export function buildSurveyLayout ({ steps, items }) {
    return steps.map((step) => ({
        id: step.id,
        items: step.surveyItemIds.map((id) => items[id]),
    }));
}

function hasSurveyWrapper (container) {
    return (container.views || []).some((child) => child.widget === 'SurveyWrapper');
}

export function destructureSurveyLayout (layout) {
    return layout.reduce(
        (acc, step) => {
            acc.items = {
                ...acc.items,
                ...keyBy(step.items, 'id'),
            };

            acc.steps.push({
                id: step.id,
                surveyItemIds: step.items.map(({ id }) => id),
            });

            return acc;
        },
        {
            steps: [],
            items: {},
        },
    );
}

export function findGuideContainer (block) {
    if (!block || typeof block !== 'object') return null;

    if (block.web?.domId === 'pendo-guide-container') return block;

    for (const child of (block.views || [])) {
        const found = findGuideContainer(child);

        if (found) return found;
    }

    return null;
}

const SURVEY_WIDGETS = new Set(['CloseButton', 'SurveyWrapper']);

function isSurveyBlock (block) {
    if (SURVEY_WIDGETS.has(block.widget)) return true;

    const domId = block.web?.domId || '';

    return domId === 'survey-button-wrapper' || domId.startsWith('survey-item-wrapper__');
}

function pushInsertionsForSurveyIndex (insertions, newSurveyIndex, existingLastSurveyIndex, maxNewSurveyIndex, newHasTrailingContent, result) {
    for (const { block, afterSurveyIndex } of insertions) {
        const wasAfterLastInExisting = afterSurveyIndex === existingLastSurveyIndex;
        const skipUseNewTrailing = wasAfterLastInExisting && newHasTrailingContent;

        if (!skipUseNewTrailing) {
            const clampedIndex = Math.min(afterSurveyIndex, maxNewSurveyIndex);

            if (clampedIndex === newSurveyIndex) {
                result.push(block);
            }
        }
    }
}

export function mergeNonSurveyBlocks (existingBuildingBlocks, newBuildingBlocks) {
    let existing;

    try {
        existing = typeof existingBuildingBlocks === 'string'
            ? JSON.parse(existingBuildingBlocks)
            : existingBuildingBlocks;
    } catch {
        return newBuildingBlocks;
    }

    const existingContainer = findGuideContainer(existing);
    const newContainer = findGuideContainer(newBuildingBlocks);

    if (!existingContainer || !newContainer) return newBuildingBlocks;

    if (!hasSurveyWrapper(newContainer)) return newBuildingBlocks;

    const existingSurveyCount = (existingContainer.views || []).filter((c) => isSurveyBlock(c)).length;
    const existingLastSurveyIndex = existingSurveyCount - 1;

    const insertions = [];
    let precedingSurveyIndex = -1;

    for (const child of (existingContainer.views || [])) {
        if (isSurveyBlock(child)) {
            precedingSurveyIndex++;
        } else {
            insertions.push({ block: child, afterSurveyIndex: precedingSurveyIndex });
        }
    }

    if (insertions.length === 0) return newBuildingBlocks;

    const newChildren = newContainer.views || [];
    const newSurveyCount = newChildren.filter((c) => isSurveyBlock(c)).length;
    const maxNewSurveyIndex = newSurveyCount - 1;

    let lastSurveyIndexInNew = -1;
    for (let i = 0; i < newChildren.length; i++) {
        if (isSurveyBlock(newChildren[i])) lastSurveyIndexInNew = i;
    }
    const newHasTrailingContent = lastSurveyIndexInNew >= 0 && lastSurveyIndexInNew < newChildren.length - 1;

    const result = [];

    for (const { block } of insertions.filter((ins) => ins.afterSurveyIndex === -1)) {
        result.push(block);
    }

    let newSurveyIndex = -1;

    for (const child of newChildren) {
        if (isSurveyBlock(child)) {
            newSurveyIndex++;
        }

        result.push(child);

        if (isSurveyBlock(child)) {
            pushInsertionsForSurveyIndex(
                insertions,
                newSurveyIndex,
                existingLastSurveyIndex,
                maxNewSurveyIndex,
                newHasTrailingContent,
                result,
            );
        }
    }

    newContainer.views = result;

    return newBuildingBlocks;
}

const PRESERVED_STEP_FIELDS = [
    'id',
    'guideId',
    'type',
    'pageId',
    'regexUrlRule',
    'elementPathRule',
    'triggerElementPathRule',
    'confirmationElementPathRule',
    'rank',
    'pollIds',
    'advanceMethod',
];

function serializeBlocks (blocks) {
    return {
        buildingBlocks: JSON.stringify(blocks),
        dom: JSON.stringify(BuildingBlock.buildingBlocksToDom(blocks)),
    };
}

export async function createGuideStepsFromLayout ({ guideId, layout, theme, guidePosition, snoozeEnabled = false, snoozeText, existingSteps = [] }) {
    const buildingBlocksForSteps = BuildingBlockLayouts.createSurveyBlocks({
        layout,
        guidePosition,
        themeStyles: theme.buildingBlocks,
        platform: 'web',
        snoozeEnabled,
        snoozeText,
    });

    return Promise.all(
        buildingBlocksForSteps.map(async (stepBlocks, index) => {
            const existingStep = existingSteps[index];

            if (existingStep) {
                const newBlocksClone = JSON.parse(JSON.stringify(stepBlocks));

                const mergedBlocks = mergeNonSurveyBlocks(
                    existingStep.buildingBlocks,
                    newBlocksClone,
                );
                const preserved = Object.fromEntries(
                    PRESERVED_STEP_FIELDS.map((key) => [key, existingStep[key]]),
                );
                let serialized;

                try {
                    serialized = serializeBlocks(mergedBlocks);
                } catch {
                    serialized = serializeBlocks(stepBlocks);
                }

                return {
                    ...preserved,
                    ...serialized,
                    contentType: 'text/html',
                    attributes: {
                        ...existingStep.attributes,
                        themeId: theme.id,
                    },
                };
            }

            const step = await createStep({ guide: guideId, type: 'building-block' });
            step.attributes.themeId = theme.id;
            step.content = null;
            step.pollIds = ['foobar'];
            step.contentType = 'text/html';
            Object.assign(step, serializeBlocks(stepBlocks));

            return step;
        }),
    );
}

export function canEditSurvey (survey, field, target) {
    return survey.appIds.reduce((acc, appId) => {
        return (
            acc &&
            $can(appId)
                .entity('guide', { entity: survey, field })
                .action('edit')
                .source(survey.status)
                .target(target || 'ANY')
        );
    }, true);
}

export function canPublishSurvey (survey) {
    return survey.appIds.reduce((acc, appId) => {
        return (
            acc &&
            $can(appId)
                .entity('guide', { entity: survey })
                .action('publish')
                .source('*')
                .target('*')
        );
    }, true);
}

export function canDeleteSurvey (survey) {
    const isAdmin = store.getters['auth/isAdmin'];
    const currentUserId = store.getters['auth/userId'];
    const isOwner = survey.createdByUser.id === currentUserId;

    return isAdmin || isOwner;
}

export function canCreateSurveyInApp (appId) {
    return $can(appId)
        .entity('guide')
        .action('create')
        .source('ANY')
        .target('ANY');
}

export function getAppsWithSurveyCreatePermission (apps) {
    return apps.filter((app) => canCreateSurveyInApp(app.id));
}
